import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Formik } from 'formik';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Button from '@material-ui/core/Button';

import prizesClient from '../../../../clients/prizes';
import TextInput from '../../../../web-app-components/components/TextInput';
import ButtonSlim from '../../../../web-app-components/components/ButtonSlim';
import useDialog from '../../../../web-app-components/hooks/useDialog';
import ObjectSelectField from '../../components/ObjectSelectField';
import PrizeSpendCodesField from '../../components/PrizeSpendCodesField';
import RouteImagesField from '../../../Routes/components/Form/components/RouteImagesField';
import { prepareMultipleFiles } from '../../../Routes/utils/prepareFiles';
import { prizeImagesToForm } from '../../utils/prizeFormData';
import ConfirmRemoveDialog from './components/ConfirmRemoveDialog';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    maxWidth: 720,
  },
  submit: {
    marginTop: theme.spacing(1),
    alignSelf: 'flex-start',
  },
}));

const normalizePrizeEnabledSpendCodeId = (raw) => {
  if (raw == null) return '';
  return String(raw).trim();
};

const validate = (values) => {
  const errors = {};
  if (!(values.Name && values.Name.trim())) {
    errors.Name = 'Pavadinimas privalomas';
  }
  return errors;
};

const ExistingPrize = ({ id }) => {
  const classes = useStyles();
  const history = useHistory();
  const { path } = useRouteMatch();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const confirmRemoveDialog = useDialog();

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const item = await prizesClient.findOne(id);
        if (!cancelled) setData(item);
      } catch (error) {
        if (!cancelled) {
          enqueueSnackbar('Nepavyko gauti prizo', { variant: 'error' });
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, enqueueSnackbar]);

  const handleSubmit = async (values) => {
    try {
      const imageIds = await prepareMultipleFiles(values.PrizeImages || []);
      const objectId = values.Object && String(values.Object).trim() ? String(values.Object).trim() : null;

      await prizesClient.update(id, {
        Name: values.Name.trim(),
        Description: (values.Description || '').trim() || null,
        GoldPrice: data?.GoldPrice ?? 0,
        Distance: data?.Distance ?? 0,
        Object: objectId,
        PrizeEnabledSpendCodeId: normalizePrizeEnabledSpendCodeId(values.PrizeEnabledSpendCodeId) || null,
        PrizeImages: imageIds,
      });
      enqueueSnackbar('Prizas atnaujintas', { variant: 'success' });
      const refreshed = await prizesClient.findOne(id);
      setData(refreshed);
    } catch (error) {
      const msg =
        error?.response?.data?.error?.message ||
        error?.message ||
        'Nepavyko atnaujinti';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleRemoveClick = () => confirmRemoveDialog.setOpen(true);

  const handleRemoveConfirm = async () => {
    try {
      await prizesClient.remove(id);
      enqueueSnackbar('Prizas pašalintas', { variant: 'success' });
      const listPath = path.replace(/\/[^/]+$/, '');
      history.push(listPath);
    } catch (error) {
      const msg =
        error?.response?.data?.error?.message ||
        error?.message ||
        'Nepavyko pašalinti';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }
  if (!data) {
    return (
      <Box>
        <Typography color="error">Prizas nerastas</Typography>
      </Box>
    );
  }

  const obj = data.Object;
  const objectId = obj ? String(obj.documentId ?? obj.id ?? '') : '';

  const initialValues = {
    Name: data.Name ?? '',
    Description: data.Description ?? data.OtherInformation ?? '',
    Object: objectId,
    PrizeEnabledSpendCodeId: normalizePrizeEnabledSpendCodeId(data.PrizeEnabledSpendCodeId),
    PrizeImages: prizeImagesToForm(data.PrizeImages),
  };

  return (
    <div>
      <Box className={classes.header}>
        <Typography variant="h5">Redaguoti prizą</Typography>
        <ButtonSlim onClick={handleRemoveClick} color="secondary">
          Pašalinti
        </ButtonSlim>
      </Box>
      <Formik
        initialValues={initialValues}
        validate={validate}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit: submit,
          setFieldValue,
          isSubmitting,
        }) => {
          const selectedObjectId =
            values.Object && String(values.Object).trim() ? String(values.Object).trim() : '';

          return (
            <form onSubmit={submit} className={classes.form} noValidate>
              <TextInput
                name="Name"
                label="Pavadinimas"
                value={values.Name}
                onChange={handleChange}
                error={touched.Name && Boolean(errors.Name)}
                helperText={touched.Name && errors.Name}
                fullWidth
              />
              <TextInput
                name="Description"
                label="Aprašymas"
                value={values.Description}
                onChange={handleChange}
                multiline
                minRows={4}
                fullWidth
              />
              <ObjectSelectField
                multiple={false}
                label="Objektas (vienas)"
                value={values.Object}
                onChange={(v) => setFieldValue('Object', v)}
                error={false}
                errorText=""
              />
              <PrizeSpendCodesField
                objectDocumentId={selectedObjectId}
                selectedSpendCodeId={values.PrizeEnabledSpendCodeId}
                onSelectedSpendCodeChange={(v) => setFieldValue('PrizeEnabledSpendCodeId', v)}
                disabled={isSubmitting}
              />
              <Typography variant="subtitle2">Prizo nuotraukos</Typography>
              <RouteImagesField
                name="PrizeImages"
                label="Įkelti nuotraukas"
                accept="image/*"
                disabled={isSubmitting}
              />
              <Box className={classes.submit}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Išsaugoma...' : 'Išsaugoti'}
                </Button>
              </Box>
            </form>
          );
        }}
      </Formik>
      <ConfirmRemoveDialog
        {...confirmRemoveDialog}
        onClose={() => confirmRemoveDialog.setOpen(false)}
        onConfirm={handleRemoveConfirm}
      />
    </div>
  );
};

ExistingPrize.propTypes = {
  id: PropTypes.string.isRequired,
};

export default ExistingPrize;
